function defaultTimers() {
    return {
        requestAnimationFrame: (callback) => globalThis.requestAnimationFrame(callback),
        cancelAnimationFrame: (handle) => {
            globalThis.cancelAnimationFrame(handle);
        },
        setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
        clearTimeout: (handle) => {
            globalThis.clearTimeout(handle);
        },
        setInterval: (callback, ms) => globalThis.setInterval(callback, ms),
        clearInterval: (handle) => {
            globalThis.clearInterval(handle);
        },
        now: () => performance.now(),
    };
}
/**
 * Монтирует сцену и возвращает ручку для снятия.
 *
 * Таймеры инжектируются: тест утечки должен считать реальные регистрации, а не
 * надеяться, что глобальные функции доступны в его окружении.
 */
export async function mountScene(scene, container, config, timers = defaultTimers()) {
    let frameHandle = null;
    const timeouts = new Set();
    const intervals = new Set();
    const listeners = [];
    const deferred = [];
    const declared = [];
    /**
     * Живая конфигурация: раскладка в ней меняется на лету.
     *
     * Копия, а не переданный объект: вызывающий не должен обнаружить, что его
     * конфигурацию кто-то правит.
     */
    const liveConfig = { ...config };
    let disposed = false;
    const context = {
        container,
        config: liveConfig,
        onFrame: (callback) => {
            // Один цикл на сцену: второй вызов onFrame заменяет предыдущий, а не
            // заводит параллельный. Два цикла в одной сцене - это почти всегда
            // забытый первый, а не осознанное решение.
            if (frameHandle !== null) {
                timers.cancelAnimationFrame(frameHandle);
            }
            let previous = timers.now();
            const started = previous;
            const step = () => {
                if (disposed) {
                    return;
                }
                const now = timers.now();
                callback(now - previous, now - started);
                previous = now;
                frameHandle = timers.requestAnimationFrame(step);
            };
            frameHandle = timers.requestAnimationFrame(step);
        },
        setTimeout: (callback, ms) => {
            const handle = timers.setTimeout(() => {
                timeouts.delete(handle);
                if (!disposed) {
                    callback();
                }
            }, ms);
            timeouts.add(handle);
        },
        setInterval: (callback, ms) => {
            const handle = timers.setInterval(() => {
                if (!disposed) {
                    callback();
                }
            }, ms);
            intervals.add(handle);
        },
        addEventListener: (target, type, handler) => {
            target.addEventListener(type, handler);
            listeners.push({ target, type: type, handler });
        },
        declare: (spec, node, place) => {
            declared.push({ spec, node, place });
        },
        defer: (dispose) => {
            deferred.push(dispose);
        },
    };
    await scene.mount(context);
    return {
        id: scene.id,
        unmount: () => {
            // Идемпотентность: повторный unmount при гонке переключений не должен
            // ни падать, ни освобождать чужие ресурсы.
            if (disposed) {
                return;
            }
            disposed = true;
            if (frameHandle !== null) {
                timers.cancelAnimationFrame(frameHandle);
                frameHandle = null;
            }
            for (const handle of timeouts)
                timers.clearTimeout(handle);
            timeouts.clear();
            for (const handle of intervals)
                timers.clearInterval(handle);
            intervals.clear();
            for (const { target, type, handler } of listeners) {
                target.removeEventListener(type, handler);
            }
            listeners.length = 0;
            // Освобождение в обратном порядке регистрации: позднее обычно зависит
            // от раннего.
            for (let i = deferred.length - 1; i >= 0; i -= 1) {
                deferred[i]?.();
            }
            deferred.length = 0;
            // Объявления живут ровно столько же, сколько сцена: узлы снятой сцены
            // редактору не принадлежат.
            declared.length = 0;
            container.replaceChildren();
        },
        resources: () => ({
            frames: frameHandle === null ? 0 : 1,
            timers: timeouts.size + intervals.size,
            listeners: listeners.length,
            deferred: deferred.length,
        }),
        applyLayout: (layout) => {
            liveConfig.layout = layout;
            for (const element of declared) {
                element.place(layout);
            }
        },
        elements: () => declared.map(({ spec, node }) => {
            /**
             * Размер меряется по факту в ЛОГИЧЕСКИХ пикселях кадра.
             *
             * `offsetWidth` не годится: элемент масштабирован преобразованием
             * корня кадра, и его экранный размер зависит от окна browser source.
             * Редактор же работает в координатах кадра 1080x1920, одинаковых у
             * всех.
             */
            const width = node.offsetWidth;
            const height = node.offsetHeight;
            return { ...spec, width, height };
        }),
    };
}
//# sourceMappingURL=scene.js.map